type BrazeSdk = {
    logCustomEvent: (event: string, props?: Record<string, unknown>) => void;
    changeUser: (externalId: string) => void;
    requestImmediateDataFlush?: () => void;
    wipeData?: () => void;
    openSession?: () => void;
    getUser: () => {
        setCustomUserAttribute: (key: string, value: unknown) => void;
        setDateOfBirth: (year: number, month: number, day: number) => void;
    };
};
// Injected by theme.liquid. catalogName is the static production catalog name,
// {myshopify subdomain}_shopify_catalog, which Braze keys the synced catalog to.
type BrazePayload = {
    market?: string;
    customerId?: string | number;
    catalogName?: string;
};
declare global {
    interface Window { braze?: BrazeSdk; __braze?: BrazePayload }
}

// The Braze app embed waits 2s, then up to 5s more for its settings, before creating window.braze.
const READY_TIMEOUT_MS = 15000;
const READY_POLL_MS = 50;

// sessionStorage keys the Braze Shopify integration uses to dedup identify/alias calls.
const CUSTOMER_SESSION_KEY_PREFIX = "ab.shopify.shopify_customer_";
const CART_SESSION_KEY_PREFIX = "ab.shopify.shopify_cart_";
// Theme-owned dedup marker for identify(), holds the last market|customerId stamped.
const IDENTIFY_SESSION_KEY = "dl-braze-identify";
const LOGOUT_PENDING_KEY = "dl-braze-logout";

const Braze = (() => {
    let readyPromise: Promise<BrazeSdk> | null = null;

    const ready = () => {
        if (readyPromise) return readyPromise;
        readyPromise = new Promise((resolve, reject) => {
            if (window.braze) return resolve(window.braze);
            const start = Date.now();
            const tick = () => {
                if (window.braze) return resolve(window.braze);
                if (Date.now() - start > READY_TIMEOUT_MS) {
                    // Clear the memo so a later call polls again once the SDK attaches.
                    readyPromise = null;
                    return reject(new Error('Braze SDK unavailable'));
                }
                setTimeout(tick, READY_POLL_MS);
            };
            tick();
        });
        return readyPromise;
    };

    // flush is opt-in: conversion events (online_signup, catalog_subscription) go out
    // immediately, browsing events (category_viewed, search_performed) stay batched.
    const safeLog = async (event: string, props: Record<string, unknown>, flush = false) => {
        try {
            const sdk = await ready();
            sdk.logCustomEvent(event, props);
            if (flush) sdk.requestImmediateDataFlush?.();
        } catch {
            console.warn(`Braze SDK unavailable, ${event} not sent`);
        }
    };

    // Stamp the market and identify the current SDK user by external_id (Shopify
    // customer.id). The market goes on the anonymous session first so pre-login
    // activity (footer/popup online_signup) carries it, then again after changeUser
    // since that swaps the active profile. changeUser is only ever called with a
    // known, logged-in customer, so prior anonymous activity on this device merges
    // into the synced profile. Runs once per session per market + customer pair, so
    // the flush only fires when the profile actually changes, not on every page view.
    const identify = async (p: { market?: string; customerId?: string | number }) => {
        try {
            if (p.customerId == null && localStorage.getItem(LOGOUT_PENDING_KEY)) await logoutCleanup();
        } catch {
            /* storage blocked (private mode), no pending logout to finish */
        }
        const stamp = `${p.market ?? ""}|${p.customerId ?? ""}`;
        try {
            if (sessionStorage.getItem(IDENTIFY_SESSION_KEY) === stamp) return;
        } catch {
            /* storage blocked (private mode), identify on every load instead */
        }
        try {
            const sdk = await ready();
            if (p.market) sdk.getUser().setCustomUserAttribute("market", p.market);
            if (p.customerId != null) {
                sdk.changeUser(String(p.customerId));
                if (p.market) sdk.getUser().setCustomUserAttribute("market", p.market);
            }
            sdk.requestImmediateDataFlush?.();
            sessionStorage.setItem(IDENTIFY_SESSION_KEY, stamp);
        } catch {
            /* SDK never loaded within timeout or storage blocked, identification skipped */
        }
    };

    // Detach the Braze profile on logout. Without this the next visitor on a shared
    // device keeps firing events against the previous customer's profile until the next
    // changeUser. wipeData drops the SDK's local user state, openSession starts a fresh
    // anonymous session, and the ab.shopify.* keys are the Shopify integration's dedup
    // markers. The theme writes none today, but clearing them keeps this correct if a
    // cart alias or identify dedup is added later.
    const logoutCleanup = async () => {
        try {
            localStorage.setItem(LOGOUT_PENDING_KEY, "1");
        } catch {
            /* storage blocked or full, pending marker skipped */
        }
        try {
            const sdk = await ready();
            sdk.wipeData?.();
            sdk.openSession?.();
            localStorage.removeItem(LOGOUT_PENDING_KEY);
            sessionStorage.removeItem(IDENTIFY_SESSION_KEY);
            Object.keys(sessionStorage).forEach((key) => {
                if (key.startsWith(CUSTOMER_SESSION_KEY_PREFIX) || key.startsWith(CART_SESSION_KEY_PREFIX)) {
                    sessionStorage.removeItem(key);
                }
            });
        } catch {
            /* SDK never loaded within timeout or storage blocked, cleanup skipped */
        }
    };

    const logCategoryViewed = (p: { product_type: string; category_url: string }) =>
        safeLog("category_viewed", p);

    const logSearchPerformed = (p: { search_term: string; results_count: number }) =>
        safeLog("search_performed", p);

    const logOnlineSignup = (p: { source: string; campaign?: string }) => {
        const props: Record<string, unknown> = { source: p.source };
        if (p.campaign !== undefined) props.campaign = p.campaign;
        return safeLog("online_signup", props, true);
    };

    const logCatalogSubscription = (p: { id: string; type: string[] }) => {
        const catalogName = window.__braze?.catalogName;
        if (!catalogName) return; /* Catalog unresolved, Braze could not route the event */
        return safeLog("catalog_subscription", { id: p.id, catalog_name: catalogName, type: p.type }, true);
    };

    // Braze month is 1-12. Sets the first-class DOB attribute on the user profile.
    const setDateOfBirth = async (year: number, month: number, day: number) => {
        try {
            const sdk = await ready();
            sdk.getUser().setDateOfBirth(year, month, day);
        } catch {
            /* SDK never loaded within timeout, attribute dropped */
        }
    };

    return {
        identify,
        logoutCleanup,
        logCategoryViewed,
        logSearchPerformed,
        logOnlineSignup,
        logCatalogSubscription,
        setDateOfBirth,
    };
})();

export default Braze;
